import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, useTheme, Stack } from '@mui/material';
import { addDays, differenceInDays, startOfDay } from 'date-fns';
import EditableDateCell from '@shared/ui/common/EditableDateCell';

interface LeadTimeTimelineProps {
  departureDate: Date;
  arrivalDate: Date;
  processedDate: Date;
  onDepartureDateChange: (date: Date) => void;
  onArrivalDateChange: (date: Date) => void;
  onProcessedDateChange: (date: Date) => void;
}

export const LeadTimeTimeline: React.FC<LeadTimeTimelineProps> = ({
  departureDate: initialDepartureDate,
  arrivalDate: initialArrivalDate,
  processedDate: initialProcessedDate,
  onDepartureDateChange,
  onArrivalDateChange,
  onProcessedDateChange,
}) => {
  const theme = useTheme();

  const [departureDate, setDepartureDate] = useState(initialDepartureDate || new Date());
  const [arrivalDate, setArrivalDate] = useState(initialArrivalDate || new Date());
  const [processedDate, setProcessedDate] = useState(initialProcessedDate || new Date());

  // Sync local state with props when they change
  useEffect(() => {
    setDepartureDate(initialDepartureDate || new Date());
  }, [initialDepartureDate]);

  useEffect(() => {
    setArrivalDate(initialArrivalDate || new Date());
  }, [initialArrivalDate]);

  useEffect(() => {
    setProcessedDate(initialProcessedDate || new Date());
  }, [initialProcessedDate]);

  const updateDepartureDate = (date: Date) => {
    const newArrivalDate = addDays(date, differenceInDays(startOfDay(arrivalDate), startOfDay(departureDate)));
    updateArrivalDate(newArrivalDate);

    setDepartureDate(date);
    onDepartureDateChange(date);

  }

  const updateArrivalDate = (date: Date) => {
    const newProcessedDate = addDays(date, differenceInDays(startOfDay(processedDate), startOfDay(arrivalDate)));
    updateProcessedDate(newProcessedDate);

    setArrivalDate(date);
    onArrivalDateChange(date);
  };

  const updateProcessedDate = (date: Date) => {
    setProcessedDate(date);
    onProcessedDateChange(date);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        bgcolor: theme.palette.background.default,
      }}
    >
      <Typography variant="subtitle2" mb={2}>Lead Time</Typography>

      <List>
        <ListItem
          divider
          sx={{ justifyContent: 'space-between', p: 0 }}
        >
          <Typography variant="body2" color="text.primary">Departure Date</Typography>
          <EditableDateCell
            value={departureDate}
            onValueChange={(value: Date | undefined) => updateDepartureDate(value || new Date())}
          />
        </ListItem>


        <ListItem
          divider
          sx={{ justifyContent: 'space-between', p: 0 }}
        >
          <Typography variant="body2" color="text.primary">Arrival Date</Typography>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>

            <Typography variant="body2" color="text.secondary">
              +{differenceInDays(startOfDay(arrivalDate), startOfDay(departureDate))} day{differenceInDays(startOfDay(arrivalDate), startOfDay(departureDate)) !== 1 ? 's' : ''}
            </Typography>
            <EditableDateCell
              value={arrivalDate}
              onValueChange={(value: Date | undefined) => updateArrivalDate(value || new Date())}
              minDate={departureDate}
            />
          </Stack>
        </ListItem>


        <ListItem

          sx={{ justifyContent: 'space-between', p: 0 }}
        >
          <Typography variant="body2" color="text.primary">Processed Date</Typography>


          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>

            <Typography variant="body2" color="text.secondary">
              +{differenceInDays(startOfDay(processedDate), startOfDay(arrivalDate))} day{differenceInDays(startOfDay(processedDate), startOfDay(arrivalDate)) !== 1 ? 's' : ''}
            </Typography>

            <EditableDateCell
              value={processedDate}
              onValueChange={(value: Date | undefined) => updateProcessedDate(value || new Date())}
              minDate={arrivalDate}
            />

          </Stack>
        </ListItem>
      </List>
    </Box>
  );
}; 